import React, { useEffect, useRef, useState } from 'react';
import {
  Link,
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams
} from 'react-router-dom';
import styled from 'styled-components';
import {
  CircularProgress,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@material-ui/core';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import PageTemplateNav from '../../common/page-template/PageTemplateNav';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import TrainingTabAssign from './tabs/TrainingTabAssign';
import TrainingTabEdit from './tabs/TrainingTabEdit';
import TrainingTabInfo from './tabs/TrainingTabInfo';
import TrainingTaskStatusLabels from './components/TrainingTaskStatusLabels';
import {
  useRevertTraining,
  useUpdateTraining,
  useFindTrainingByTaskId,
  getTrainingTransitionType
} from './Training.hooks';
import {
  Training,
  TrainingTransition,
  BaseTrainingCardProps
} from './Training.interface';
import { useCheckIfHasTemplate } from '../template/Template.hooks';
import { TemplateType } from '../template/Template.interface';
import TrainingPlanExportButton from './components/TrainingPlanExportButton';

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  .training-id {
    line-height: unset;
  }
  .status-container {
    display: flex;
    margin-top: 5px;
    .status-label {
      margin-right: 8px;
    }
  }
`;

const getTabValue = (path: string): string => {
  const array: string[] = path.split('/');
  return array[array.length - 1];
};

const TrainingAdminDetailPage: React.FC = () => {
  const history = useHistory();
  const { taskId: paramTaskId = '' } = useParams<{ taskId?: string }>();
  const taskId: number = parseInt(paramTaskId) || 0;
  const tabValue: string = getTabValue(history.location.pathname);
  const updateTaskRef = useRef<(() => void) | undefined>();
  const [training, setTraining] = useState<Training | undefined>();
  const {
    training: loadedTraining,
    isLoading: isFinding,
    error: findError
  } = useFindTrainingByTaskId(taskId, true);
  const {
    training: updatedTraining,
    isLoading: isUpdating,
    error: updateError,
    patch: updateTraining
  } = useUpdateTraining(training?.id || 0);
  const {
    training: revertedTraining,
    isLoading: isReverting,
    error: revertError,
    revert: revertTraining
  } = useRevertTraining(training?.id || 0);
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const isLoading: boolean = isFinding || isUpdating || isReverting;
  const allErrors: FetchError | undefined =
    findError || updateError || revertError;

  const [{ data: ifHasTemplate }, get] = useCheckIfHasTemplate(
    TemplateType.TrainingPlan
  );

  const cardProps: BaseTrainingCardProps = {
    training: training!,
    onChange: (newTraining: Partial<Training>) => {
      training && setTraining({ ...training, ...newTraining });
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const onChangeTask = (task: Task) => {
    if (!training) {
      return;
    }
    const transition: TrainingTransition = getTrainingTransitionType(
      training.task,
      task
    );
    // update training task
    training.task = task;
    setTraining({ ...training });
    // update training version
    switch (transition) {
      case TrainingTransition.Approve:
        updateTraining({ shouldUpdateStatus: true });
        break;
    }
  };

  // check and revert training before updating task
  const onBeforeUpdateTask = (task: Task, updateTask: () => void) => {
    if (!training) {
      updateTask();
      return;
    }
    updateTaskRef.current = updateTask;
    const transition: TrainingTransition = getTrainingTransitionType(
      training.task,
      task
    );
    switch (transition) {
      case TrainingTransition.Revert:
        revertTraining();
        break;
      default:
        updateTaskRef.current = undefined;
        updateTask();
    }
  };

  useEffect(() => {
    if (loadedTraining) {
      setTraining(loadedTraining);
    }
  }, [loadedTraining]);

  useEffect(() => {
    if (updatedTraining) {
      setTraining(updatedTraining);
    }
  }, [updatedTraining]);

  useEffect(() => {
    if (revertedTraining) {
      updateTaskRef.current?.();
      updateTaskRef.current = undefined;
      setTraining(revertedTraining);
    }
  }, [revertedTraining]);

  useEffect(() => {
    if (revertError) {
      updateTaskRef.current = undefined;
    }
  }, [revertError]);

  useEffect(() => {
    setError(allErrors);
  }, [allErrors]);

  useEffect(() => {
    get();
  }, [get]);

  if (!['info', 'edit', 'assign'].includes(tabValue)) {
    // if navigating to /portal/admin/training/task/:taskId redirect to edit tab
    return <Redirect to={`/portal/admin/training/task/${taskId}/info`} />;
  }

  return (
    <PageTemplate>
      <PageTemplateHeader>
        {isLoading && <CircularProgress size={20} />}
        {training && (
          <StyledTitle>
            <Typography variant='overline' className='training-id'>
              TRAINING-{training.id}
            </Typography>
            <h2>{training.name}</h2>
            <div className='status-container'>
              <TrainingTaskStatusLabels training={training} />
            </div>
          </StyledTitle>
        )}
        <PageTemplateHeaderActions flex>
          {training?.task && (
            <TaskStatusActions
              task={training.task}
              taskEntity={training}
              onChange={onChangeTask}
              onBeforeUpdateTask={onBeforeUpdateTask}
            />
          )}
          <Tooltip
            title={
              !ifHasTemplate
                ? 'There are no templates configured for this item. Please contact your administrator'
                : ''
            }
            placement='left'
            arrow
          >
            <div
              style={{
                marginLeft: 10
              }}
            >
              <TrainingPlanExportButton
                training={training}
                buttonProps={{ disabled: !training }}
              />
            </div>
          </Tooltip>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateNav>
        <Tabs
          variant='scrollable'
          indicatorColor='primary'
          scrollButtons='auto'
          value={tabValue}
        >
          <Tab
            component={Link}
            label='Training Info'
            value='info'
            to='./info'
          />
          <Tab
            component={Link}
            label='Edit Training'
            value='edit'
            to='./edit'
          />
          <Tab
            component={Link}
            label='Assign Training'
            value='assign'
            to='./assign'
          />
        </Tabs>
      </PageTemplateNav>
      <PageTemplateDashboard>
        <Switch>
          <Route exact path='/portal/admin/training/task/:taskId/info'>
            {training && <TrainingTabInfo {...cardProps} />}
          </Route>
          <Route exact path='/portal/admin/training/task/:taskId/edit'>
            {training && <TrainingTabEdit {...cardProps} />}
          </Route>
          <Route exact path='/portal/admin/training/task/:taskId/assign'>
            {training && <TrainingTabAssign trainingId={training.id} />}
          </Route>
        </Switch>
      </PageTemplateDashboard>

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Training details updated.
      </AppSnackbar>
    </PageTemplate>
  );
};

export default TrainingAdminDetailPage;
